import { useRouter } from 'next/router'
import { useCallback, useMemo, useState } from 'react'
import { createContainer } from 'unstated-next'

import { useFetchBulk } from '../hooks/useFetchBulk'
import { CategoryIndices, GenderKey, VisualFilterTile } from '../types'

type InitialState = {
  gender: GenderKey
  categoryFilters: Array<VisualFilterTile>
}

const useBulkContainer = (
  initialState: InitialState = { gender: 'women', categoryFilters: [] }
) => {
  const { gender, categoryFilters } = initialState
  const router = useRouter()
  const [currentGender, setCurrentGender] = useState<GenderKey>(gender)
  const [categoryIndices, setCategoryIndices] = useState<CategoryIndices>({
    women: 0,
    men: 0,
  })

  const currentCategoryIndex = categoryIndices[currentGender]
  const currentCategoryTile = useMemo(
    () => categoryFilters[currentCategoryIndex],
    [categoryFilters, currentCategoryIndex]
  )

  const { loading, error, pageInfo, fetchMore, categorizedData } = useFetchBulk({
    gender: currentGender,
    categoryTile: currentCategoryTile!,
  })

  const moveToNextCategory = useCallback(() => {
    const nextIndex = currentCategoryIndex + 1
    if (nextIndex < categoryFilters.length) {
      setCategoryIndices(prev => ({
        ...prev,
        [currentGender]: nextIndex,
      }))
    }
  }, [currentCategoryIndex, categoryFilters.length, currentGender])

  const paginate = useCallback(async () => {
    // Don't paginate if we're loading
    if (loading) return

    // If we're at the end, move to next category but ensure loading is false
    if (!pageInfo?.hasNextPage || !pageInfo.endCursor) {
      moveToNextCategory()
      return
    }

    try {
      await fetchMore({
        variables: { after: pageInfo?.endCursor! },
      })
    } catch (error) {
      console.error('Error fetching more:', error)
    }
  }, [pageInfo?.hasNextPage, pageInfo?.endCursor, moveToNextCategory, fetchMore, loading])

  const handleGenderChange = useCallback(
    (newGender: GenderKey) => {
      if (newGender === currentGender) return
      setCurrentGender(newGender)
      router.replace({ query: { gender: newGender } }, undefined, { shallow: true, scroll: false })
    },
    [currentGender, router]
  )

  return useMemo(
    () => ({
      loading,
      error,
      pageInfo,
      categoryFilters,
      currentGender,
      currentCategoryFilter: currentCategoryTile?.value ?? '',
      currentCategoryIndex,
      handleGenderChange,
      paginate,
      categorizedData,
    }),
    [
      loading,
      error,
      pageInfo,
      categoryFilters,
      currentGender,
      currentCategoryTile,
      currentCategoryIndex,
      handleGenderChange,
      paginate,
      categorizedData,
    ]
  )
}

export const BulkContainer = createContainer(useBulkContainer)
